/**
 * Error → toast bridge.
 *
 * Surfaces `FetchError` (and the occasional stray `Error`) as a coss
 * toast via the `toastManager` singleton in `components/ui/toast.tsx`.
 *
 * Canonical format:
 *   title:    `${err.code}` (uppercase), falling back to status-based
 *             labels (`UNAUTHORIZED`, `FORBIDDEN`, `NOT_FOUND`, etc.)
 *             or `ERROR` for non-FetchError throws.
 *   description: `err.message` — already human-readable on every
 *             apps/api error envelope.
 *   action:   "Copy request id" when the error carries one. Copies
 *             `err.request_id` to the clipboard.
 *
 * NOT called directly from mutation `onError` in most cases — the
 * `MutationCache.onError` handler on the root QueryClient routes
 * every 4xx/5xx here for us. Use `toastFromError` ad-hoc if you need
 * to surface an error that didn't come through react-query.
 */
import { toastManager } from '@/components/ui/toast';
import { FetchError } from './api';

/**
 * Build a human label from the error. Prefers the backend-provided
 * `code` (`VALIDATION_FAILED`, `PLAN_LIMIT_EXCEEDED`, …) because that
 * matches what runbooks reference.
 */
function titleFor(err: unknown): string {
  if (err instanceof FetchError) {
    if (err.code) return err.code;
    const byStatus: Record<number, string> = {
      400: 'BAD_REQUEST',
      401: 'UNAUTHENTICATED',
      402: 'PLAN_LIMIT_EXCEEDED',
      403: 'FORBIDDEN',
      404: 'NOT_FOUND',
      409: 'CONFLICT',
      422: 'VALIDATION_FAILED',
      429: 'RATE_LIMITED',
    };
    return byStatus[err.status] ?? `HTTP_${err.status}`;
  }
  return 'ERROR';
}

function messageFor(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong';
}

function requestIdFor(err: unknown): string | undefined {
  if (err instanceof FetchError && err.request_id) return err.request_id;
  return undefined;
}

async function copyToClipboard(value: string): Promise<void> {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }
  } catch {
    /* fall through */
  }
  // Legacy fallback — hidden textarea + execCommand. Kept for Safari
  // private-mode + any browser that refuses clipboard perms.
  const ta = document.createElement('textarea');
  ta.value = value;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '-1000px';
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(ta);
  }
}

export interface ToastFromErrorOptions {
  /**
   * Override the default title (the error code). Useful when a
   * mutation wants to prefix with the action — "Revoke failed" etc.
   */
  title?: string;
}

/**
 * Demo read-only toast. A blocked write can route here from BOTH the
 * `MutationCache` handler and a hook's own `onError`, and a single form
 * may fire several writes — throttle so they collapse to one friendly
 * toast rather than a stack of "FORBIDDEN"s.
 */
let lastDemoToastAt = 0;
function demoReadOnlyToast(): void {
  const now = Date.now();
  if (now - lastDemoToastAt < 2500) return;
  lastDemoToastAt = now;
  toastManager.add({
    type: 'info',
    title: 'Demo account',
    description:
      "You can't make changes in the demo — sign up for a free account to try this for real.",
  });
}

/**
 * Public entry so non-apiFetch demo blocks (the Clerk stub's password /
 * 2FA / profile methods) surface the same friendly toast.
 */
export function notifyDemoBlocked(): void {
  demoReadOnlyToast();
}

/**
 * Show a toast for an error. Swallows the error (returns void). Safe
 * to call from every `onError` handler.
 */
export function toastFromError(err: unknown, options: ToastFromErrorOptions = {}): void {
  // Blocked demo write → friendly, deduped info toast instead of a raw
  // FORBIDDEN error (the `demo_read_only` error is thrown by apiFetch).
  if (err instanceof FetchError && err.code === 'demo_read_only') {
    demoReadOnlyToast();
    return;
  }
  const title = options.title ?? titleFor(err);
  const description = messageFor(err);
  const requestId = requestIdFor(err);

  toastManager.add({
    type: 'error',
    title,
    description,
    ...(requestId
      ? {
          actionProps: {
            children: 'Copy request id',
            onClick: () => {
              void copyToClipboard(requestId);
            },
          },
        }
      : {}),
  });
}

/**
 * Show a "success" toast. Thin wrapper used by mutations so callers
 * don't have to import the toastManager directly.
 */
export function toastSuccess(title: string, description?: string): void {
  toastManager.add({ type: 'success', title, ...(description ? { description } : {}) });
}

/**
 * Show an error toast with a custom title + description. Use this
 * instead of `toastFromError` when the error isn't an unexpected
 * server failure — e.g. when a surfaced outcome (test-fire
 * rejection, plan-limit hit surfaced as copy rather than a 402) is
 * already shaped for the user and a request-id copy button would be
 * unhelpful noise.
 */
export function toastError(title: string, description?: string): void {
  toastManager.add({ type: 'error', title, ...(description ? { description } : {}) });
}

/**
 * Show a plain info toast.
 */
export function toastInfo(title: string, description?: string): void {
  toastManager.add({ type: 'info', title, ...(description ? { description } : {}) });
}

/**
 * `toastPromise` — show a loading toast for a mutation and upgrade
 * it to success/error when the promise settles.
 *
 * Used by long-running dashboard actions (run-audit, bulk delete,
 * session revocation, CSV export) where the fire-and-forget toast
 * pattern leaves the user wondering if their click registered.
 *
 * Resolves to the same value the promise resolved to / re-throws the
 * same error — call-site behaviour is unchanged. The toast is the
 * only side-effect.
 *
 * Error toasts get the same "Copy request id" action as
 * `toastFromError` when the error is a `FetchError`.
 */
export function toastPromise<T>(
  promise: Promise<T>,
  options: {
    loading: string;
    success: string | ((value: T) => string);
    error?: string | ((err: unknown) => string);
    loadingDescription?: string;
    successDescription?: string | ((value: T) => string | undefined);
  },
): Promise<T> {
  return toastManager.promise(promise, {
    loading: {
      type: 'loading',
      title: options.loading,
      ...(options.loadingDescription ? { description: options.loadingDescription } : {}),
    },
    success: (value) => {
      const title =
        typeof options.success === 'function' ? options.success(value) : options.success;
      const descRaw =
        typeof options.successDescription === 'function'
          ? options.successDescription(value)
          : options.successDescription;
      return {
        type: 'success',
        title,
        ...(descRaw ? { description: descRaw } : {}),
      };
    },
    error: (err) => {
      const title =
        typeof options.error === 'function' ? options.error(err) : (options.error ?? titleFor(err));
      const description = messageFor(err);
      const requestId = requestIdFor(err);
      return {
        type: 'error',
        title,
        description,
        ...(requestId
          ? {
              actionProps: {
                children: 'Copy request id',
                onClick: () => {
                  void copyToClipboard(requestId);
                },
              },
            }
          : {}),
      };
    },
  });
}
